/**
 * `FindAddress` is a presentational component used by `AddressManager` to render
 * either:
 * - a postcode lookup input (with optional address dropdown), or
 * - a manual address entry form.
 *
 * Conditional rendering notes:
 * - Postcode lookup is only available when the configured `countryCode` is
 *   `CountryIsoCodes.GB`.
 * - The lookup panel is shown when `showPostcodeLookUp` is true.
 * - The manual entry form is shown when `showAddressForm` is true, or when
 *   postcode lookup is unavailable.
 * - The “Enter address manually” button is only shown when lookup is available
 *   and manual entry is allowed.
 *
 * Interaction is callback-driven; the parent owns state and passes current
 * values (`address`, `addressList`, `selectedAddress`) and any validation
 * messages (`addressErrors`, `searchPostCodeError`).
 */
import React from 'react';
//@ts-ignore
import TextButtonInput from '../TextButtonInput/TextButtonInput';
import { AddressDetails, SelectOpt } from '../CommonTypes';
//@ts-ignore
import './FindAddress.scss';
import { ButtonCommand } from '../Button/ButtonCommand';
import SelectInput from '../SelectInput/SelectInput';
import TextInput from '../TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import { CountryIsoCodes } from '../Constants';
import { useAppSelector } from '../../../app-state/Hooks';

type FindAddressProps = {
  findAddressTitle: string;
  showAddressList: boolean;
  showAddressForm: boolean;
  addressList: SelectOpt[];
  selectedAddress: string;
  addressErrors?: AddressDetails;
  address?: AddressDetails;
  searchPostCode?: string;
  searchPostCodeError?: string;
  finding: boolean;
  countries: SelectOpt[];
  showPostcodeLookUp: boolean;
  findAddressLabel: string;
  onSelectAddress: (selectedAddressMoniker: string) => void;
  onChangeDetail: (
    event: React.FormEvent<HTMLInputElement>,
    key: string
  ) => void;
  onShowAddressFormClick: () => void;
  onFindClick: () => void;
  onChangeSearchPostCode: (event: React.FormEvent<HTMLInputElement>) => void;
  onCountryChange: (country: string) => void;
  onShowPostcodeLookupClick: () => void;
  allowManualEntry: boolean;
  isMandatoryAddressline2?: boolean;
};

const FindAddress = ({
  findAddressTitle,
  showAddressList,
  showAddressForm,
  addressList,
  selectedAddress,
  addressErrors,
  address,
  searchPostCode,
  searchPostCodeError,
  finding,
  countries,
  showPostcodeLookUp,
  findAddressLabel,
  onSelectAddress,
  onChangeDetail,
  onShowAddressFormClick,
  onFindClick,
  onChangeSearchPostCode,
  onCountryChange,
  onShowPostcodeLookupClick,
  allowManualEntry,
  isMandatoryAddressline2
}: FindAddressProps): JSX.Element => {
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);
  const postcodeSearchAvailble =
    countryCode === CountryIsoCodes.GB ? true : false;

  const { t } = useTranslation();

  const selectAddressLabel = t('personal-details.select-address-label');
  const address1Label = t('personal-details.address1-label');
  const optionalFieldLabel = t('personal-details.optional-field-label');
  const address2Label = t('personal-details.address2-label');
  const cityLabel = t('personal-details.city-label');
  const countyLabel = t('personal-details.county-label');
  const countryLabel = t('personal-details.country-label');
  const postcodeLabel = t('personal-details.postcode-label');
  const buttonAddressLookupText = t(
    'personal-details.button-address-lookup-text'
  );

  const getAddressList = (): JSX.Element => {
    let listPanel: JSX.Element = <></>;

    if (showAddressList === true) {
      listPanel = (
        <div className='inline-control'>
          <label className='control-label' htmlFor='address-list'>
            {selectAddressLabel}
          </label>
          <div className='control'>
            <SelectInput
              name='address-list'
              forceDefault
              className='input-text w250'
              options={addressList}
              onChange={onSelectAddress}
              value={selectedAddress}
              error={addressErrors?.addressList}
            />
          </div>
        </div>
      );
    }
    return listPanel;
  };

  const getAddressForm = (): JSX.Element => {
    let addressSection: JSX.Element = <></>;

    const countryOption = countries.filter(
      (c) => c.id.toLowerCase() === countryCode
    );

    if (showAddressForm === true || postcodeSearchAvailble === false) {
      addressSection = (
        <>
          <div className='flex-row'>
            <div className='inline-control'>
              <label className='control-label' htmlFor='addressLine1'>
                {address1Label}
              </label>
              <div className='control'>
                <TextInput
                  name='addressLine1'
                  placeholder={address1Label}
                  className='input-text w250'
                  error={addressErrors?.addressLine1}
                  value={address?.addressLine1 || ''}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeDetail(e, 'addressLine1')
                  }
                />
              </div>
            </div>

            <div className='inline-control'>
              <label className='control-label' htmlFor='addressLine2'>
                {address2Label}
                {!isMandatoryAddressline2 && (
                  <span className='info-text m02-left'>
                    {optionalFieldLabel}
                  </span>
                )}
              </label>
              <div className='control'>
                <TextInput
                  className='input-text w250'
                  name='addressLine2'
                  placeholder={address2Label}
                  error={addressErrors?.addressLine2}
                  value={address?.addressLine2 || ''}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeDetail(e, 'addressLine2')
                  }
                />
              </div>
            </div>
          </div>

          <div className='flex-row'>
            <div className='inline-control'>
              <label className='control-label' htmlFor='townOrCity'>
                {cityLabel}
              </label>
              <div className='control'>
                <TextInput
                  className='input-text w250'
                  name='townOrCity'
                  placeholder={cityLabel}
                  error={addressErrors?.townOrCity}
                  value={address?.townOrCity || ''}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeDetail(e, 'townOrCity')
                  }
                />
              </div>
            </div>

            <div className='inline-control'>
              <label className='control-label' htmlFor='county'>
                {countyLabel}
                <span className='info-text m02-left'>{optionalFieldLabel}</span>
              </label>
              <div className='control'>
                <TextInput
                  name='county'
                  placeholder={countyLabel}
                  className='input-text w250'
                  error={addressErrors?.region}
                  value={address?.region || ''}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeDetail(e, 'region')
                  }
                />
              </div>
            </div>
          </div>

          <div className='flex-row'>
            <div className='inline-control'>
              <label className='control-label' htmlFor='country'>
                {countryLabel}
              </label>
              <div className='control'>
                <SelectInput
                  name='country'
                  loadingText='Select country'
                  className='input-text w250'
                  value={address?.country ?? ''}
                  options={countryOption}
                  onChange={onCountryChange}
                  defaultText={t('personal-details.country-default')}
                  forceDefault
                  defaultIfOneOption
                  addDefaultOption
                  error={addressErrors?.country}
                />
              </div>
            </div>

            <div className='inline-control'>
              <label className='control-label' htmlFor='postcode'>
                {postcodeLabel}
              </label>
              <div className='control'>
                <TextInput
                  name='postcode'
                  placeholder={postcodeLabel}
                  className='input-text w250'
                  error={addressErrors?.postCode}
                  value={address?.postCode || ''}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeDetail(e, 'postCode')
                  }
                />
              </div>
            </div>
          </div>

          {postcodeSearchAvailble === true && showPostcodeLookUp === false && (
            <div className='flex-row m2-top m1-bottom'>
              <p>
                {findAddressLabel}&nbsp;
                <ButtonCommand
                  name='btn-show-address-look-up'
                  text={buttonAddressLookupText}
                  additionalClassNames='font-normal'
                  onClick={onShowPostcodeLookupClick}
                />
              </p>
            </div>
          )}
        </>
      );
    }
    return addressSection;
  };

  const getManualAddressButton = (): JSX.Element => {
    if (
      postcodeSearchAvailble === false ||
      showAddressForm === true ||
      allowManualEntry === false
    ) {
      return <></>;
    }

    return (
      <div className='flex-row'>
        <div className='control control-label'>
          <ButtonCommand
            name='show-address-form'
            text={t('personal-details.enter-address-manually')}
            additionalClassNames='small'
            onClick={onShowAddressFormClick}
          />
        </div>
      </div>
    );
  };

  const addressListPanel = getAddressList();
  const addressPanel = getAddressForm();
  const manualAddressButtonPanel = getManualAddressButton();

  return (
    <>
      {postcodeSearchAvailble === true && showPostcodeLookUp === true && (
        <div className='flex-row'>
          <div className='inline-control'>
            <div className='control-label'>{findAddressTitle}</div>
            <div className='control postcode-finder'>
              <TextButtonInput
                haslabel
                themeClass='button-theme-find-address-small'
                name='find-address'
                label={findAddressTitle}
                error={searchPostCodeError}
                value={searchPostCode}
                onChange={onChangeSearchPostCode}
                onButtonClick={onFindClick}
                buttonText={t('personal-details.find-address')}
                waitValue={finding}
                placeholder={t('personal-details.enter-postcode')}
                className='postcode-finder input-text'
                buttonDisabled={finding}
                buttonStyle={'button-basic cms-btn-find btn-find btn-postcode'}
              />
            </div>
          </div>

          {addressListPanel}
        </div>
      )}
      {manualAddressButtonPanel}
      {addressPanel}
    </>
  );
};

export default FindAddress;
